import React, { useMemo, useState } from 'react';
import {
  GripVertical,
  Trash2,
  Edit2,
  Check,
  X,
  Layers,
  Search,
} from 'lucide-react';
import type { BuilderSection } from 'types/websiteBuilder';
import { BUILDER_TEMPLATES } from '../builderTemplates';
import ConfirmDeleteSectionModal from '../ConfirmDeleteSectionModal';
import SectionEditorPanel from '../SectionEditorPanel';
import styles from './ActiveSectionsPanel.module.scss';

function getSectionTypeLabel(type: BuilderSection['type']) {
  if (type === 'cta') return 'CTA';
  if (type === 'faq') return 'FAQ';
  if (type === 'campaignHero') return 'Campaign Hero';
  if (type === 'navigationTiles') return 'Navigation Tiles';
  if (type === 'marquee') return 'Slider';
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function getTemplateLabel(type: BuilderSection['type']): string {
  const t = BUILDER_TEMPLATES.find((tpl) => tpl.key === type);
  return t?.label ?? getSectionTypeLabel(type);
}

function getSectionCategory(type: BuilderSection['type']): string {
  const t = BUILDER_TEMPLATES.find((tpl) => tpl.key === type);
  return t?.category ?? 'Other';
}

interface ActiveSectionsPanelProps {
  sections: BuilderSection[];
  selectedSectionId: string | null;
  onSelectSection: (id: string | null) => void;
  onUpdateSectionData: (id: string, data: Record<string, unknown>) => void;
  onUpdateSectionLabel: (id: string, label: string) => void;
  onReorder: (sections: BuilderSection[]) => void;
  onRemoveSection: (id: string) => void;
  onRequestHide?: () => void;
}

function ActiveSectionsPanel({
  sections,
  selectedSectionId,
  onSelectSection,
  onUpdateSectionData,
  onUpdateSectionLabel,
  onReorder,
  onRemoveSection,
  onRequestHide,
}: ActiveSectionsPanelProps) {
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [tempLabel, setTempLabel] = useState('');
  const [query, setQuery] = useState('');
  const [dragSectionId, setDragSectionId] = useState<string | null>(null);
  const [dragOverSectionId, setDragOverSectionId] = useState<string | null>(
    null,
  );

  const orderedSections = useMemo(
    () => [...sections].sort((a, b) => a.order - b.order),
    [sections],
  );

  const frameSorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orderedSections;
    return orderedSections.filter((s) => {
      const label = (s.customLabel || getTemplateLabel(s.type)).toLowerCase();
      const type = getSectionTypeLabel(s.type).toLowerCase();
      return label.includes(q) || type.includes(q);
    });
  }, [orderedSections, query]);

  const startEditingLabel = (section: BuilderSection) => {
    setEditingLabelId(section.id);
    setTempLabel(section.customLabel || getTemplateLabel(section.type));
  };

  const saveLabel = (id: string) => {
    onUpdateSectionLabel(id, tempLabel);
    setEditingLabelId(null);
  };

  const handleDragStart =
    (sectionId: string) => (e: React.DragEvent<HTMLButtonElement>) => {
      setDragSectionId(sectionId);
      setDragOverSectionId(sectionId);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', sectionId);
    };

  const handleDragOver =
    (sectionId: string) => (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragOverSectionId !== sectionId) {
        setDragOverSectionId(sectionId);
      }
    };

  const handleDrop =
    (targetSectionId: string) => (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      const fallbackId = e.dataTransfer.getData('text/plain');
      const sourceSectionId = dragSectionId ?? fallbackId;
      if (!sourceSectionId || sourceSectionId === targetSectionId) {
        setDragSectionId(null);
        setDragOverSectionId(null);
        return;
      }

      const next = [...orderedSections];
      const fromIndex = next.findIndex((s) => s.id === sourceSectionId);
      const toIndex = next.findIndex((s) => s.id === targetSectionId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
        setDragSectionId(null);
        setDragOverSectionId(null);
        return;
      }

      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      onReorder(next.map((s, i) => ({ ...s, order: i })));
      setDragSectionId(null);
      setDragOverSectionId(null);
    };

  const handleDragEnd = () => {
    setDragSectionId(null);
    setDragOverSectionId(null);
  };

  const renderItem = (section: BuilderSection) => {
    const label = section.customLabel || getTemplateLabel(section.type);
    const isSelected = selectedSectionId === section.id;
    const isEditing = editingLabelId === section.id;
    const idx = frameSorted.findIndex((s) => s.id === section.id);
    const isDragging = dragSectionId === section.id;
    const isDragOver = dragOverSectionId === section.id && !isDragging;

    return (
      <li
        key={section.id}
        className={`${styles.item} ${isSelected ? styles.itemSelected : ''} ${
          isDragging ? styles.itemDragging : ''
        } ${isDragOver ? styles.itemDragOver : ''}`}
        onDragOver={handleDragOver(section.id)}
        onDrop={handleDrop(section.id)}
      >
        <div className={styles.itemRow}>
          {isEditing ? (
            <div className={styles.labelEdit}>
              <input
                className={styles.labelInput}
                value={tempLabel}
                onChange={(e) => setTempLabel(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveLabel(section.id);
                  if (e.key === 'Escape') setEditingLabelId(null);
                }}
              />
              <button
                type="button"
                className={styles.iconBtnGreen}
                onClick={() => saveLabel(section.id)}
                aria-label="Save label"
              >
                <Check size={13} />
              </button>
              <button
                type="button"
                className={styles.iconBtnRed}
                onClick={() => setEditingLabelId(null)}
                aria-label="Cancel edit"
              >
                <X size={13} />
              </button>
            </div>
          ) : (
            <div className={styles.labelWrapper}>
              <span className={styles.orderNum}>{idx + 1}</span>
              <button
                type="button"
                className={`${styles.labelBtn} ${isSelected ? styles.labelBtnSelected : ''}`}
                onClick={() => onSelectSection(isSelected ? null : section.id)}
              >
                <span className={styles.labelText}>{label}</span>
                <span className={styles.typeChip}>
                  {getSectionTypeLabel(section.type)}
                </span>
              </button>
              <button
                type="button"
                className={styles.editBtn}
                onClick={() => startEditingLabel(section)}
                aria-label="Edit label"
                title="Rename"
              >
                <Edit2 size={11} />
              </button>
            </div>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.actionBtn}
              draggable
              onDragStart={handleDragStart(section.id)}
              onDragEnd={handleDragEnd}
              aria-label="Drag to reorder section"
              title="Drag to reorder"
            >
              <GripVertical size={14} />
            </button>
            <button
              type="button"
              className={`${styles.actionBtn} ${styles.deleteBtn}`}
              onClick={() => setDeleteTarget({ id: section.id, label })}
              aria-label="Delete section"
              title="Delete"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>

        {isSelected && (
          <div className={styles.editorArea}>
            <SectionEditorPanel
              section={section}
              onUpdateData={(data) => onUpdateSectionData(section.id, data)}
            />
          </div>
        )}
      </li>
    );
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <Layers size={15} className={styles.headerIcon} />
        <span className={styles.headerTitle}>Active Sections</span>
        <span className={styles.headerCount}>{sections.length}</span>
        {onRequestHide && (
          <button
            type="button"
            className={styles.headerBtn}
            onClick={onRequestHide}
            aria-label="Hide active sections panel"
            title="Hide"
          >
            {'<'}
          </button>
        )}
      </div>

      <div className={styles.toolbar}>
        <div className={styles.search}>
          <Search size={14} className={styles.searchIcon} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={styles.searchInput}
            placeholder="Search sections…"
            aria-label="Search active sections"
          />
          {query.trim() && (
            <button
              type="button"
              className={styles.clearBtn}
              onClick={() => setQuery('')}
              aria-label="Clear search"
              title="Clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {frameSorted.length > 0 ? (
        <ul className={styles.list}>
          {frameSorted.flatMap((section, index) => {
            const category = getSectionCategory(section.type);
            const prevCategory =
              index > 0
                ? getSectionCategory(frameSorted[index - 1].type)
                : null;
            const showCategoryHeading =
              prevCategory === null || prevCategory !== category;

            const heading = showCategoryHeading ? (
              <li
                key={`category-${category}-${section.id}`}
                className={styles.categoryHeading}
                aria-label={`${category} sections`}
              >
                <span className={styles.categoryLabel}>{category}</span>
              </li>
            ) : null;

            const item = renderItem(section);
            return heading ? [heading, item] : [item];
          })}
        </ul>
      ) : (
        <div className={styles.empty}>
          {query.trim() ? (
            <>
              <p>No matches.</p>
              <p className={styles.emptyHint}>Try a different search.</p>
            </>
          ) : (
            <>
              <p>No sections yet.</p>
              <p className={styles.emptyHint}>
                Add sections from the library on the left.
              </p>
            </>
          )}
        </div>
      )}

      <ConfirmDeleteSectionModal
        show={deleteTarget !== null}
        onHide={() => setDeleteTarget(null)}
        sectionLabel={deleteTarget?.label ?? 'section'}
        onConfirm={() => {
          if (!deleteTarget) return;
          const { id } = deleteTarget;
          setDeleteTarget(null);
          onRemoveSection(id);
        }}
      />
    </div>
  );
}

export default ActiveSectionsPanel;
